/* eslint-disable react/jsx-no-target-blank */
import React from 'react'
import moment from 'moment'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import NumberFormat from 'react-number-format'
import styled from 'styled-components'

import Checkbox from '../../../../components/Checkbox'

import { mediaQuery as media } from '../../../../utils/mediaQuery'

import { schema } from './validations'
import alert from '../../../../assets/images/svg/ic-alert-circle.svg'

interface IProps {
    document: any
    onSubmit: () => void
    onBack: () => void
}

const ConfirmationWrapper = styled.div``

const ConfirmationContainer = styled.div`
    width: 100%;
    max-width: 564px;
    margin: 0px auto;
    box-sizing: border-box;
    padding: 40px 16px 80px;
`

const TitleContainer = styled.div`
    padding-bottom: 24px;
`

const Title = styled.div`
    font-style: normal;
    font-weight: 800;
    font-size: 16px;
    line-height: 24px;
    color: #36393D;
    text-align: center;
    ${media.md`
        font-size: 24px;
        line-height: 32px;
    `}
`

const DetailContainer = styled.div`
    background: #FFFFFF;
    box-shadow: 0px 8px 24px rgba(40, 79, 171, 0.1);
    border-radius: 8px;
    padding: 12px 12px 32px;
    ${media.md`
        padding: 32px 24px 44px;
    `}
`

const InfoContainer = styled.div`
    padding-bottom: 12px;
    border-bottom: 1px solid #BECAE6;
    &:not(:first-child) {
        padding-top: 12px;
    }
    ${media.sm`
        display: flex;
        justify-content: space-between;
        padding-bottom: 8px;
        &:not(:first-child) {
            padding-top: 12px;
        }
    `}
`

const LabelContainer = styled.div``

const Label = styled.div`
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    color: #6884C4;
    margin-bottom: 4px;
    ${media.md`
        font-size: 16px;
        line-height: 24px;
    `}
`

const ValueContainer = styled.div``

const Value = styled.div`
    font-style: normal;
    font-weight: 800;
    font-size: 14px;
    line-height: 20px;
    color: #36393D;
    text-transform: capitalize;
    ${media.md`
        font-size: 16px;
        line-height: 24px;
    `}
`

const ErrorContainer = styled.div`
    display: flex;
    align-items: center;
    background: #F6EEEC;
    border: 0.5px solid #CEA7A4;
    box-sizing: border-box;
    border-radius: 3px;
    padding: 18px;
    margin-bottom: 24px;
`

const AlertContainer = styled.div``

const Alert = styled.img``

const ParagraphContainer = styled.div`
    max-width: 763px;
    margin-left: 15px;
`

const Paragraph = styled.div`
    font-style: normal;
    font-weight: 800;
    font-size: 14px;
    line-height: 20px;
    color: #970C0C;
`

const TermsContainer = styled.div`
    padding: 19px 12px;
    background: #FFFFFF;
    box-shadow: 0px 8px 24px rgba(40, 79, 171, 0.1);
    border-radius: 8px;
    margin-top: 24px;
    ${media.md`
        padding: 24px;
    `}
`

const CheckboxContainer = styled.div``

const ButtonContainer = styled.div`
    margin-top: 40px;
    ${media.md`
        max-width: 364px;
        margin: 0px auto;
        margin-top: 48px;
    `}
`

const Submit = styled.button`
    width: 100%;
    border: none;
    border: 1px solid #CC26BA;
    background: #CC26BA;
    border-radius: 4px;
    font-family: Nunito, sans-serif;
    font-style: normal;
    font-weight: 800;
    font-size: 16px;
    line-height: 24px;
    color: #FFFFFF;
    padding: 12px 0px;
    cursor: pointer;
    &:disabled {
        color: #82949E;
        background: #E6EAEC;
        border: 1px solid #82949E;
    }
`

const Button = styled.button`
    width: 100%;
    border: 1px solid #36393D;
    border-radius: 4px;
    font-family: Nunito, sans-serif;
    font-style: normal;
    font-weight: 800;
    font-size: 16px;
    line-height: 24px;
    color: #36393D;
    padding: 12px 0px;
    margin-top: 16px;
    cursor: pointer;
`

const ConfirmationPage = ({
    document,
    onSubmit,
    onBack
}: IProps) => {
    const methods = useForm({
        mode: 'onChange',
        reValidateMode: 'onSubmit',
        resolver: yupResolver(schema())
    })
    const { handleSubmit, formState: { isValid } } = methods

    return (
        <React.Fragment>
            <ConfirmationWrapper>
                <ConfirmationContainer>
                    <FormProvider {...methods}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <TitleContainer>
                                <Title>Revisa los detalles de tu pago</Title>
                            </TitleContainer>
                            {document?.eSTADOField === 'PAGADA' && (
                                <ErrorContainer>
                                    <AlertContainer>
                                        <Alert src={alert} />
                                    </AlertContainer>
                                    <ParagraphContainer>
                                        <Paragraph>El número de documento ya se encuentra pagado.</Paragraph>
                                    </ParagraphContainer>
                                </ErrorContainer>
                            )}
                            <DetailContainer>
                                <InfoContainer>
                                    <LabelContainer>
                                        <Label>N° documento</Label>
                                    </LabelContainer>
                                    <ValueContainer>
                                        <Value>{document?.bELNRField}</Value>
                                    </ValueContainer>
                                </InfoContainer>
                                <InfoContainer>
                                    <LabelContainer>
                                        <Label>Proyecto</Label>
                                    </LabelContainer>
                                    <ValueContainer>
                                        <Value>{document?.pROYECTOField?.toLowerCase()}</Value>
                                    </ValueContainer>
                                </InfoContainer>
                                <InfoContainer>
                                    <LabelContainer>
                                        <Label>Ubicación</Label>
                                    </LabelContainer>
                                    <ValueContainer>
                                        <Value>{document?.pRODUCTOField}</Value>
                                    </ValueContainer>
                                </InfoContainer>
                                <InfoContainer>
                                    <LabelContainer>
                                        <Label>Vencimiento</Label>
                                    </LabelContainer>
                                    <ValueContainer>
                                        <Value>{document?.fECHAVENCIMIENTOField ? moment(document?.fECHAVENCIMIENTOField).format('DD/MM/YYYY') : ''}</Value>
                                    </ValueContainer>
                                </InfoContainer>
                                <InfoContainer>
                                    <LabelContainer>
                                        <Label>{`Monto en ${document?.mONEDAField === 'USD' ? 'dólares' : 'soles'}`}</Label>
                                    </LabelContainer>
                                    <ValueContainer>
                                        <Value>
                                            <NumberFormat
                                                prefix={document?.mONEDAField === 'USD' ? 'US$' : 'S/'}
                                                decimalSeparator='.'
                                                displayType='text'
                                                type='text'
                                                thousandSeparator={true}
                                                decimalScale={2}
                                                fixedDecimalScale={true}
                                                value={(document?.iMPORTEField - document?.ImportePago)}
                                            />
                                        </Value>
                                    </ValueContainer>
                                </InfoContainer>
                            </DetailContainer>
                            {document?.eSTADOField !== 'PAGADA' && (
                                <TermsContainer>
                                    <CheckboxContainer>
                                        <Checkbox name='terms'>
                                            Acepto las <a target='_blank' href='https://storagecdnvivienda.blob.core.windows.net/vivienda/sistemas/micuenta/micuenta.politica.privacidad.pdf'>Políticas de Privacidad</a> y <a target='_blank' href='https://storagecdnvivienda.blob.core.windows.net/vivienda/sistemas/micuenta/micuenta.terminos.condiciones.pdf'>Términos y condiciones</a>
                                        </Checkbox>
                                    </CheckboxContainer>
                                </TermsContainer>
                            )}
                            <ButtonContainer>
                                <Submit type='submit' disabled={!isValid}>
                                    Realizar pago
                                </Submit>
                                <Button type='button' onClick={onBack}>Regresar</Button>
                            </ButtonContainer>
                        </form>
                    </FormProvider>
                </ConfirmationContainer>
            </ConfirmationWrapper>
        </React.Fragment>
    )
}

export default ConfirmationPage